// @ts-check
import gsap from 'gsap';

/**
 * Custom cursor: a small dot + trailing ring that replaces the default
 * pointer on desktop.
 *
 * Positioning and hover/click scaling both live in GSAP's `transform` cache
 * (x / y / xPercent / yPercent / scale). Keeping them in ONE channel means
 * they compose correctly — a standalone CSS `translate` property is NOT used
 * because older engines ignore it and pin the cursor to its top-left origin.
 *
 * The native cursor is hidden via `html.has-custom-cursor`, a class that is
 * only added AFTER this component mounts successfully. So if it never mounts
 * (touch-only device, reduced-motion, a JS error), the native cursor simply
 * stays visible — the user can never end up with no cursor at all.
 */
export class CustomCursor {
  constructor() {
    /** @type {HTMLElement|null} */
    this.dot = null;
    /** @type {HTMLElement|null} */
    this.ring = null;

    // Enable only when there is a precise, hovering pointer (a real mouse or
    // trackpad). Deliberately NOT keyed on `maxTouchPoints` — touch-capable
    // laptops report touch points even though the user drives a mouse, and
    // disabling there left those users cursor-less.
    const finePointer = window.matchMedia(
      '(hover: hover) and (pointer: fine)'
    ).matches;
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    if (!finePointer || reducedMotion) return;

    const dot = document.querySelector('.cursor-dot');
    const ring = document.querySelector('.cursor-ring');
    if (!dot || !ring) return;
    this.dot = dot;
    this.ring = ring;

    const root = document.documentElement;
    // Hide the native cursor only now that we know we mounted.
    root.classList.add('has-custom-cursor');

    // Centre the element on the pointer via the transform cache. xPercent/
    // yPercent keep it centred regardless of the element's own size; x/y are
    // the live pointer coordinates.
    gsap.set([dot, ring], {
      xPercent: -50,
      yPercent: -50,
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
      opacity: 0,
    });

    // High-frequency position updates. The dot tracks instantly; the ring
    // trails with a soft ease for the signature lag.
    const setDotX = gsap.quickSetter(dot, 'x', 'px');
    const setDotY = gsap.quickSetter(dot, 'y', 'px');
    const setRingX = gsap.quickTo(ring, 'x', {
      duration: 0.5,
      ease: 'power3.out',
    });
    const setRingY = gsap.quickTo(ring, 'y', {
      duration: 0.5,
      ease: 'power3.out',
    });

    let hasMoved = false;

    /** @param {MouseEvent} e */
    const onMove = (e) => {
      setDotX(e.clientX);
      setDotY(e.clientY);
      setRingX(e.clientX);
      setRingY(e.clientY);
    };

    // Centre the cursor on first move, then show and switch to the fast path.
    /** @param {MouseEvent} e */
    const onFirstMove = (e) => {
      onMove(e);
      hasMoved = true;
      gsap.to([dot, ring], {
        opacity: 1,
        duration: 0.3,
        ease: 'power2.out',
      });
      document.removeEventListener('mousemove', onFirstMove);
      document.addEventListener('mousemove', onMove, { passive: true });
    };
    document.addEventListener('mousemove', onFirstMove, { passive: true });

    // ── Hover: ring expands, dot shrinks on interactive targets ──────
    const interactive = document.querySelectorAll(
      'a, button, input, textarea, select, [data-cursor]'
    );
    /** @param {Event} e */
    const onHoverIn = () => {
      gsap.to(ring, {
        scale: 1.6,
        borderColor: 'rgba(212, 163, 115, 0.55)',
        duration: 0.35,
        ease: 'power2.out',
      });
      gsap.to(dot, {
        scale: 0.5,
        opacity: 0.4,
        duration: 0.35,
        ease: 'power2.out',
      });
    };
    /** @param {Event} e */
    const onHoverOut = () => {
      gsap.to(ring, {
        scale: 1,
        borderColor: 'rgba(255, 255, 255, 0.4)',
        duration: 0.35,
        ease: 'power2.out',
      });
      gsap.to(dot, {
        scale: 1,
        opacity: 1,
        duration: 0.35,
        ease: 'power2.out',
      });
    };
    interactive.forEach((el) => {
      el.addEventListener('mouseenter', onHoverIn);
      el.addEventListener('mouseleave', onHoverOut);
    });

    // ── Click feedback ───────────────────────────────────────────────
    /** @param {Event} e */
    const onDown = () =>
      gsap.to(ring, {
        scale: 0.75,
        duration: 0.1,
        ease: 'power2.in',
      });
    /** @param {Event} e */
    const onUp = () =>
      gsap.to(ring, {
        scale: 1,
        duration: 0.35,
        ease: 'power2.out',
      });
    document.addEventListener('mousedown', onDown);
    document.addEventListener('mouseup', onUp);

    // ── Window leave / enter ─────────────────────────────────────────
    const onDocumentLeave = () => {
      gsap.to([dot, ring], { opacity: 0, duration: 0.2 });
    };
    const onDocumentEnter = () => {
      if (!hasMoved) return;
      gsap.to([dot, ring], { opacity: 1, duration: 0.2 });
    };
    document.addEventListener('mouseleave', onDocumentLeave);
    document.addEventListener('mouseenter', onDocumentEnter);

    // Expose cleanup for destroy().
    this._cleanup = () => {
      root.classList.remove('has-custom-cursor');
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mousemove', onFirstMove);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('mouseleave', onDocumentLeave);
      document.removeEventListener('mouseenter', onDocumentEnter);
      interactive.forEach((el) => {
        el.removeEventListener('mouseenter', onHoverIn);
        el.removeEventListener('mouseleave', onHoverOut);
      });
    };

    /** Flag so destroy can tell if we ever mounted. */
    this._mounted = true;
  }

  /** Tear down — remove listeners and hide cursor elements. */
  destroy() {
    if (!this._mounted || !this.dot || !this.ring) return;
    this._cleanup?.();
    gsap.to([this.dot, this.ring], { opacity: 0, duration: 0.1 });
  }
}
